'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useTheme } from '@/components/ThemeProvider';
import { useAuth } from '@/contexts/AuthContext';

export default function MemberStoreHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [cartCount] = useState(2);
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const router = useRouter();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-sm'
          : 'bg-transparent'
      }`}
    >
      {/* Attribution banner */}
      <div className="bg-primary/10 border-b border-primary/20 py-1.5 px-4 text-center">
        <p className="text-xs text-primary font-medium">
          You are shopping Maria Santos's Richésse Store — purchases support Maria directly.{' '}
          <Link href="/" className="underline hover:no-underline">Learn more</Link>
        </p>
      </div>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Brand */}
          <Link href="/" className="flex items-center gap-2.5">
            <AppLogo size={32} />
            <div>
              <span className="font-display text-base font-semibold tracking-wide text-foreground">RICHÉSSE</span>
              <span className="block text-[10px] text-muted-foreground -mt-0.5">Maria's Store</span>
            </div>
          </Link>

          {/* Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {[
              { id: 'store-nav-home', label: 'Home', href: '/member-personal-store' },
              { id: 'store-nav-products', label: 'All Products', href: '/product-catalog' },
              { id: 'store-nav-wellness', label: 'Wellness', href: '/product-catalog' },
              { id: 'store-nav-beauty', label: 'Beauty', href: '/product-catalog' },
              { id: 'store-nav-about', label: 'About Maria', href: '#about' },
              { id: 'store-nav-contact', label: 'Contact', href: '#contact' },
            ]?.map(link => (
              <Link
                key={link?.id}
                href={link?.href}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {link?.label}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors"
              aria-label="Toggle theme"
            >
              <Icon name={theme === 'dark' ? 'SunIcon' : 'MoonIcon'} size={18} className="text-muted-foreground" />
            </button>
            <button className="relative w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors" aria-label="Shopping cart">
              <Icon name="ShoppingBagIcon" size={20} className="text-foreground" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            {user ? (
              <Link
                href="/member-dashboard"
                className="hidden sm:flex btn-primary text-xs font-semibold px-4 py-2 rounded-lg"
              >
                Dashboard
              </Link>
            ) : (
              <Link
                href="/sign-up-login-screen"
                className="hidden sm:flex btn-primary text-xs font-semibold px-4 py-2 rounded-lg"
              >
                Join Richésse
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}